/* eslint-disable react/no-danger, jsx-a11y/no-noninteractive-element-interactions */
import * as React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, TooltipProps } from 'recharts';
import AutoSizer from 'react-virtualized-auto-sizer';
import { getTheme, ITheme, mergeStyleSets } from '@fluentui/react/lib/Styling';
import { formStyles } from 'styles/formStyles';
import classnames from 'classnames';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import moment from 'moment-timezone';
import { createRecordUrl, formatDecimal, formatMoney, formatPercent } from 'utils/formatter';
import { useSelector } from 'react-redux';
import { lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import { Kunde, useKundeList } from 'api/kunde';
import { OpenLink } from 'formComponents/OpenLink/OpenLink';
import { BetreuerById } from 'formComponents/lookups/BetreuerById';
import { FilterValues } from './types';
import { FilterInfo } from './FilterInfo';
import { HochrechnungData, useHochrechnungData } from './hooks/useHochrechnungData';

const theme: ITheme = getTheme();
const { palette } = theme;

const odd = palette.neutralLight;
const even = palette.neutralLighterAlt;

interface ChartDataRow {
    month: string;
    vkNetto0: number;
    vkNetto1: number;
    vkNettoPercent0: number;
    vkNettoPercent1: number;
    nMarge0: number;
    nMarge1: number;
    nMargePercent0: number;
    nMargePercent1: number;
}

export const styles = mergeStyleSets({
    container: {
        width: '100%',
        height: 'calc(100vh - 120px)',
        overflowY: 'auto',
    },
    printContainer: {
        width: '100%',
        height: '100%',
        fontSize: '12px',
    },
    summaryTable: {
        marginTop: 25,
        width: '100%',
        borderSpacing: 0,
    },
    table: {
        marginTop: 25,
        width: '100%',
        // border: '1px solid black',
        borderSpacing: 0,
    },
    theadTd1: {
        width: '20%',
        height: 30,
        borderBottom: '1px solid black',
    },
    theadTd: {
        fontWeight: 700,
        height: 30,
        width: 'calc(85% / 13)',
        borderBottom: '1px solid black',
    },
    tbodyTd1: {
        width: '20%',
        height: 30,
        // borderBottom: '1px solid black',
    },
    tbodyTd: {
        height: 30,
        // borderBottom: '1px solid black',
    },
    summaryTheadTd1: {
        width: '20%',
        height: 30,
        borderBottom: '1px solid black',
    },
    summaryTheadTd: {
        fontWeight: 700,
        height: 30,
        width: 'calc(80% / 9)',
        borderBottom: '1px solid black',
    },
    borderBottom: {
        borderBottom: '1px solid black',
    },
    bold: {
        fontWeight: 700,
    },
    odd: {
        backgroundColor: odd,
    },
    even: {
        backgroundColor: even,
    },
    kundeListContainer: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
    },
    chart: {
        width: '100%',
        height: 400,
    },
    tooltip: {
        backgroundColor: '#FFF',
        padding: 10,
    },
    tooltipHeader: {
        fontWeight: 700,
        padding: 5,
        textAlign: 'right',
    },
    tooltipValue: {
        textAlign: 'right',
        padding: 5,
    },
    extrapolated: {
        fontWeight: 700,
        fontStyle: 'italic',
        fontSize: 16,
        color: palette.green,
    },
    positiveGrowth: {
        color: palette.green,
    },
    negativeGrowth: {
        color: palette.red,
    },
    kundeList: {
        maxHeight: 300,
        overflowY: 'scroll',
        overflowX: 'hidden',
    },
    kundeListTitle: {
        marginBottom: 0,
        paddingBottom: 0,
    },
    kundeListSubTitle: {
        marginBottom: 10,
        display: 'inline-block',
    },
    betreuerSearch: {
        display: 'grid',
        gridTemplateColumns: '1fr 250px',
        marginTop: 10,
    },
});

interface HochrechnungProps extends FilterValues {
    printView?: boolean;
}

export const Hochrechnung: React.FC<HochrechnungProps> = (filterValues) => {
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);

    const [betreuerId, setBetreuerId] = React.useState(0);

    const { sourceEntity, period, printView = false } = filterValues;

    const datumVon =
        period === 'calendaryear'
            ? moment('2026-04-01').startOf('year')
            : moment('2026-04-01').month() <= 6
            ? moment('2026-04-01').startOf('year').subtract({ months: 6 })
            : moment('2026-04-01').startOf('year').add({ months: 6 });

    const hochrechnungDataJ1 = useHochrechnungData(
        {
            sourceEntity,
            period,
        },
        datumVon.clone().subtract({ years: 1 }).format('YYYY-MM-DD'),
    );

    const hochrechnungDataJ0 = useHochrechnungData(
        {
            sourceEntity,
            period,
        },
        datumVon.format('YYYY-MM-DD'),
        hochrechnungDataJ1,
    );

    const kundenJ1 = useKundeList(
        {
            nrList: hochrechnungDataJ1.kNrList,
            includeArchiv: true,
        },
        { enabled: !hochrechnungDataJ0.loading && !hochrechnungDataJ1.loading },
    );

    const filteredKundenJ1 = React.useMemo<Kunde[]>(() => {
        if (!betreuerId) {
            return kundenJ1.data || [];
        }
        return (kundenJ1.data || []).filter((k) => k.betreuer === betreuerId || k.telBetreuer === betreuerId);
    }, [betreuerId, kundenJ1.data]);

    const kundenJ0 = useKundeList(
        {
            nrList: hochrechnungDataJ0.kNrList,
            includeArchiv: true,
        },
        { enabled: !hochrechnungDataJ0.loading && !hochrechnungDataJ1.loading },
    );

    const filteredKundenJ0 = React.useMemo<Kunde[]>(() => {
        if (!betreuerId) {
            return kundenJ0.data || [];
        }
        return (kundenJ0.data || []).filter((k) => k.betreuer === betreuerId || k.telBetreuer === betreuerId);
    }, [betreuerId, kundenJ0.data]);

    const kundenOnlyJ1 = React.useMemo(
        () => filteredKundenJ1.filter((k1) => !filteredKundenJ0.find((k0) => k0.nr === k1.nr)),
        [filteredKundenJ0, filteredKundenJ1],
    );

    const kundenOnlyJ0 = React.useMemo(
        () => filteredKundenJ0.filter((k0) => !filteredKundenJ1.find((k1) => k1.nr === k0.nr)),
        [filteredKundenJ0, filteredKundenJ1],
    );

    const kundenBoth = React.useMemo(() => {
        const kundenBothJ0 = filteredKundenJ0.filter((k0) => filteredKundenJ1.find((k1) => k1.nr === k0.nr));
        const kundenBothJ1 = filteredKundenJ1.filter((k1) => filteredKundenJ0.find((k0) => k0.nr === k1.nr));

        return kundenBothJ0.concat(kundenBothJ1.filter((k1) => !kundenBothJ0.find((k0) => k0.nr === k1.nr)));
    }, [filteredKundenJ0, filteredKundenJ1]);

    const sourceEntityLabel = sourceEntity === 'auftrag' ? 'Auftrag' : 'Rechnung';
    const sourceEntityLabelPlural = sourceEntity === 'auftrag' ? 'Aufträge' : 'Rechnungen';
    const sourceEntityLabelPlural2 = sourceEntity === 'auftrag' ? 'Aufträgen' : 'Rechnungen';

    const renderKundeList = (kundeList: Kunde[]) => {
        return (
            <div className={styles.kundeList}>
                {kundeList.map((kunde) => (
                    <OpenLink
                        key={kunde.nr}
                        link={createRecordUrl(
                            sourceEntity === 'auftrag' ? 'auftraege' : 'rechnungen',
                            kunde.nr.toString(),
                        )}
                        label={kunde.name1}
                    />
                ))}
            </div>
        );
    };

    /*
    const renderSummaryRow = (hochrechnungData: HochrechnungData, complete: boolean, extrapolated: boolean) => {
        return (
            <tr>
                <td className={classnames(styles.tbodyTd, styles.borderBottom)}>
                    {hochrechnungData.months[0].month.substring(0, 7)}
                    {' - '}
                    {hochrechnungData.months[11].month.substring(0, 7)}
                    {complete ? '' : extrapolated ? ' (hochgerechnet)' : ' (bis Vormonat)'}
                </td>

                {extrapolated ? (
                    <>
                        <td
                            className={classnames(
                                styles.tbodyTd,
                                formStyles.dataValueRight,
                                styles.borderBottom,
                                styles.extrapolated,
                            )}
                        >
                            {formatDecimal(hochrechnungData.kundenTotal, 0)}
                        </td>
                        <td
                            className={classnames(
                                styles.tbodyTd,
                                formStyles.dataValueRight,
                                styles.borderBottom,
                                styles.extrapolated,
                            )}
                        >
                            {formatDecimal(hochrechnungData.recordsTotal, 0)}
                        </td>
                        <td
                            className={classnames(
                                styles.tbodyTd,
                                formStyles.dataValueRight,
                                styles.borderBottom,
                                styles.extrapolated,
                            )}
                        >
                            {formatDecimal(hochrechnungData.recordsTotal / hochrechnungData.kundenTotal)}
                        </td>
                        <td
                            className={classnames(
                                styles.tbodyTd,
                                formStyles.dataValueRight,
                                styles.borderBottom,
                                styles.extrapolated,
                            )}
                        >
                            {formatMoney(hochrechnungData.vkNettoTotal / hochrechnungData.kundenTotal, 2)}
                        </td>
                        <td
                            className={classnames(
                                styles.tbodyTd,
                                formStyles.dataValueRight,
                                styles.borderBottom,
                                styles.extrapolated,
                            )}
                        >
                            {formatMoney(hochrechnungData.vkNettoTotal / hochrechnungData.recordsTotal, 2)}
                        </td>
                        <td
                            className={classnames(
                                styles.tbodyTd,
                                formStyles.dataValueRight,
                                styles.borderBottom,
                                styles.extrapolated,
                            )}
                        >
                            {formatMoney(hochrechnungData.nMargeTotal / hochrechnungData.kundenTotal, 2)}
                        </td>
                        <td
                            className={classnames(
                                styles.tbodyTd,
                                formStyles.dataValueRight,
                                styles.borderBottom,
                                styles.extrapolated,
                            )}
                        >
                            {formatMoney(hochrechnungData.nMargeTotal / hochrechnungData.recordsTotal, 2)}
                        </td>
                    </>
                ) : (
                    <>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatDecimal(hochrechnungData.kundenTotalOriginal, 0)}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatDecimal(hochrechnungData.recordsTotalOriginal, 0)}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatDecimal(
                                hochrechnungData.recordsTotalOriginal / hochrechnungData.kundenTotalOriginal,
                            )}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatMoney(
                                hochrechnungData.vkNettoTotalOriginal / hochrechnungData.kundenTotalOriginal,
                                2,
                            )}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatMoney(
                                hochrechnungData.vkNettoTotalOriginal / hochrechnungData.recordsTotalOriginal,
                                2,
                            )}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatMoney(
                                hochrechnungData.nMargeTotalOriginal / hochrechnungData.kundenTotalOriginal,
                                2,
                            )}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatMoney(
                                hochrechnungData.nMargeTotalOriginal / hochrechnungData.recordsTotalOriginal,
                                2,
                            )}
                        </td>
                    </>
                )}
            </tr>
        );
    };
     */

    const renderSummary = () => {
        const kundenProportionalTotalDiffPercent =
            (hochrechnungDataJ0.kundenProportionalTotal - hochrechnungDataJ1.kundenProportionalTotal) /
            (hochrechnungDataJ1.kundenProportionalTotal / 100);

        const recordsProportionalTotalDiffPercent =
            (hochrechnungDataJ0.recordsProportionalTotal - hochrechnungDataJ1.recordsProportionalTotal) /
            (hochrechnungDataJ1.recordsProportionalTotal / 100);

        const recordsPerKundeJ0 =
            hochrechnungDataJ0.recordsProportionalTotal / hochrechnungDataJ0.kundenProportionalTotal;
        const recordsPerKundeJ1 =
            hochrechnungDataJ1.recordsProportionalTotal / hochrechnungDataJ1.kundenProportionalTotal;

        const recordsPerKundeDiffPercent = (recordsPerKundeJ0 - recordsPerKundeJ1) / (recordsPerKundeJ1 / 100);

        const vkNettoDiffPercent =
            (hochrechnungDataJ0.vkNettoProportionalTotal - hochrechnungDataJ1.vkNettoProportionalTotal) /
            (hochrechnungDataJ1.vkNettoProportionalTotal / 100);

        const vkNettoPerKundeJ0 =
            hochrechnungDataJ0.vkNettoProportionalTotal / hochrechnungDataJ0.kundenProportionalTotal;
        const vkNettoPerKundeJ1 =
            hochrechnungDataJ1.vkNettoProportionalTotal / hochrechnungDataJ1.kundenProportionalTotal;

        const vkNettoPerKundeDiffPercent = (vkNettoPerKundeJ0 - vkNettoPerKundeJ1) / (vkNettoPerKundeJ1 / 100);

        const vkNettoPerRecordJ0 =
            hochrechnungDataJ0.vkNettoProportionalTotal / hochrechnungDataJ0.recordsProportionalTotal;
        const vkNettoPerRecordJ1 =
            hochrechnungDataJ1.vkNettoProportionalTotal / hochrechnungDataJ1.recordsProportionalTotal;

        const vkNettoPerRecordDiffPercent = (vkNettoPerRecordJ0 - vkNettoPerRecordJ1) / (vkNettoPerRecordJ1 / 100);

        const nMargeDiffPercent =
            (hochrechnungDataJ0.nMargeProportionalTotal - hochrechnungDataJ1.nMargeProportionalTotal) /
            (hochrechnungDataJ1.nMargeProportionalTotal / 100);

        const nMargePerKundeJ0 =
            hochrechnungDataJ0.nMargeProportionalTotal / hochrechnungDataJ0.kundenProportionalTotal;
        const nMargePerKundeJ1 =
            hochrechnungDataJ1.nMargeProportionalTotal / hochrechnungDataJ1.kundenProportionalTotal;

        const nMargePerKundeDiffPercent = (nMargePerKundeJ0 - nMargePerKundeJ1) / (nMargePerKundeJ1 / 100);

        const nMargePerRecordJ0 =
            hochrechnungDataJ0.nMargeProportionalTotal / hochrechnungDataJ0.recordsProportionalTotal;
        const nMargePerRecordJ1 =
            hochrechnungDataJ1.nMargeProportionalTotal / hochrechnungDataJ1.recordsProportionalTotal;

        const nMargePerRecordDiffPercent = (nMargePerRecordJ0 - nMargePerRecordJ1) / (nMargePerRecordJ1 / 100);

        return (
            <table className={styles.summaryTable}>
                <thead>
                    <tr>
                        <td className={styles.summaryTheadTd1} />
                        <td className={classnames(styles.summaryTheadTd, formStyles.dataValueRight)}>Kaufkunden</td>
                        <td className={classnames(styles.summaryTheadTd, formStyles.dataValueRight)}>
                            {sourceEntityLabelPlural}
                        </td>
                        <td className={classnames(styles.summaryTheadTd, formStyles.dataValueRight)}>
                            {sourceEntityLabelPlural}/Kunde Ø
                        </td>
                        <td className={classnames(styles.summaryTheadTd, formStyles.dataValueRight)}>VK-Netto</td>
                        <td className={classnames(styles.summaryTheadTd, formStyles.dataValueRight)}>
                            VK-Netto/Kunde Ø
                        </td>
                        <td className={classnames(styles.summaryTheadTd, formStyles.dataValueRight)}>
                            VK-Netto/{sourceEntityLabel} Ø
                        </td>
                        <td className={classnames(styles.summaryTheadTd, formStyles.dataValueRight)}>Netto-Marge</td>
                        <td className={classnames(styles.summaryTheadTd, formStyles.dataValueRight)}>
                            Netto-Marge/Kunde Ø
                        </td>
                        <td className={classnames(styles.summaryTheadTd, formStyles.dataValueRight)}>
                            Netto-Marge/{sourceEntityLabel} Ø
                        </td>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className={classnames(styles.tbodyTd, styles.borderBottom)}>
                            {hochrechnungDataJ0.months[0].month.substring(0, 7)}
                            {' - '}
                            {hochrechnungDataJ0.proportionalEndMonth}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatDecimal(hochrechnungDataJ0.kundenProportionalTotal, 0)}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatDecimal(hochrechnungDataJ0.recordsProportionalTotal, 0)}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatDecimal(recordsPerKundeJ0, 2)}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatMoney(hochrechnungDataJ0.vkNettoProportionalTotal)}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatMoney(vkNettoPerKundeJ0, 2)}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatMoney(vkNettoPerRecordJ0, 2)}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatMoney(hochrechnungDataJ0.nMargeProportionalTotal)}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatMoney(nMargePerKundeJ0, 2)}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatMoney(nMargePerRecordJ0, 2)}
                        </td>
                    </tr>
                    <tr>
                        <td className={classnames(styles.tbodyTd, styles.borderBottom)} />
                        <td
                            className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom, {
                                [styles.positiveGrowth]: kundenProportionalTotalDiffPercent > 0,
                                [styles.negativeGrowth]: kundenProportionalTotalDiffPercent < 0,
                            })}
                        >
                            {formatPercent(kundenProportionalTotalDiffPercent, 2)}
                        </td>
                        <td
                            className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom, {
                                [styles.positiveGrowth]: recordsProportionalTotalDiffPercent > 0,
                                [styles.negativeGrowth]: recordsProportionalTotalDiffPercent < 0,
                            })}
                        >
                            {formatPercent(recordsProportionalTotalDiffPercent, 2)}
                        </td>
                        <td
                            className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom, {
                                [styles.positiveGrowth]: kundenProportionalTotalDiffPercent > 0,
                                [styles.negativeGrowth]: kundenProportionalTotalDiffPercent < 0,
                            })}
                        >
                            {formatPercent(recordsPerKundeDiffPercent, 2)}
                        </td>
                        <td
                            className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom, {
                                [styles.positiveGrowth]: vkNettoDiffPercent > 0,
                                [styles.negativeGrowth]: vkNettoDiffPercent < 0,
                            })}
                        >
                            {formatPercent(vkNettoDiffPercent, 2)}
                        </td>
                        <td
                            className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom, {
                                [styles.positiveGrowth]: vkNettoPerKundeDiffPercent > 0,
                                [styles.negativeGrowth]: vkNettoPerKundeDiffPercent < 0,
                            })}
                        >
                            {formatPercent(vkNettoPerKundeDiffPercent, 2)}
                        </td>
                        <td
                            className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom, {
                                [styles.positiveGrowth]: vkNettoPerRecordDiffPercent > 0,
                                [styles.negativeGrowth]: vkNettoPerRecordDiffPercent < 0,
                            })}
                        >
                            {formatPercent(vkNettoPerRecordDiffPercent, 2)}
                        </td>
                        <td
                            className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom, {
                                [styles.positiveGrowth]: nMargeDiffPercent > 0,
                                [styles.negativeGrowth]: nMargeDiffPercent < 0,
                            })}
                        >
                            {formatPercent(nMargeDiffPercent, 2)}
                        </td>
                        <td
                            className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom, {
                                [styles.positiveGrowth]: nMargePerKundeDiffPercent > 0,
                                [styles.negativeGrowth]: nMargePerKundeDiffPercent < 0,
                            })}
                        >
                            {formatPercent(nMargePerKundeDiffPercent, 2)}
                        </td>
                        <td
                            className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom, {
                                [styles.positiveGrowth]: nMargePerRecordDiffPercent > 0,
                                [styles.negativeGrowth]: nMargePerRecordDiffPercent < 0,
                            })}
                        >
                            {formatPercent(nMargePerRecordDiffPercent, 2)}
                        </td>
                    </tr>
                    <tr>
                        <td className={classnames(styles.tbodyTd, styles.borderBottom)}>
                            {hochrechnungDataJ1.months[0].month.substring(0, 7)}
                            {' - '}
                            {hochrechnungDataJ1.proportionalEndMonth}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatDecimal(hochrechnungDataJ1.kundenProportionalTotal, 0)}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatDecimal(hochrechnungDataJ1.recordsProportionalTotal, 0)}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatDecimal(recordsPerKundeJ1, 2)}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatMoney(hochrechnungDataJ1.vkNettoProportionalTotal)}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatMoney(vkNettoPerKundeJ1, 2)}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatMoney(vkNettoPerRecordJ1, 2)}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatMoney(hochrechnungDataJ1.nMargeProportionalTotal)}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatMoney(nMargePerKundeJ1, 2)}
                        </td>
                        <td className={classnames(styles.tbodyTd, formStyles.dataValueRight, styles.borderBottom)}>
                            {formatMoney(nMargePerRecordJ1, 2)}
                        </td>
                    </tr>
                </tbody>
            </table>
        );
    };

    const renderHochrechnungData = (hochrechnungData: HochrechnungData, current: boolean) => {
        return (
            <table className={styles.table}>
                <thead>
                    <tr>
                        <td className={styles.theadTd1}>Monat</td>
                        {hochrechnungData.months.map((month, index) => (
                            <td
                                key={month.month}
                                className={classnames(
                                    styles.theadTd,
                                    formStyles.dataValueCenter,
                                    index % 2 ? styles.even : styles.odd,
                                )}
                            >
                                {month.month.substring(0, 7)}
                            </td>
                        ))}
                        <td className={classnames(styles.theadTd, formStyles.dataValueRight)}>Gesamt</td>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className={styles.tbodyTd1}>VK-Netto pro Monat</td>
                        {hochrechnungData.months.map((month, index) => (
                            <td
                                key={month.month}
                                className={classnames(
                                    styles.tbodyTd,
                                    formStyles.dataValueRight,
                                    index % 2 ? styles.even : styles.odd,
                                    { [styles.extrapolated]: month.vkNettoExtrapolated },
                                )}
                            >
                                {formatMoney(month.vkNetto)}
                            </td>
                        ))}
                        <td
                            className={classnames(styles.tbodyTd, formStyles.dataValueRight, {
                                [styles.extrapolated]: current,
                            })}
                        >
                            {formatMoney(hochrechnungData.vkNettoTotal)}
                        </td>
                    </tr>
                    <tr>
                        <td className={classnames(styles.tbodyTd1, styles.borderBottom)} />

                        {hochrechnungData.months.map((month, index) => (
                            <td
                                key={month.month}
                                className={classnames(
                                    styles.tbodyTd,
                                    formStyles.dataValueRight,
                                    styles.borderBottom,
                                    index % 2 ? styles.even : styles.odd,
                                    { [styles.extrapolated]: month.vkNettoPercentExtrapolated },
                                )}
                            >
                                {formatPercent(month.vkNettoPercent, 2)}
                            </td>
                        ))}
                        <td className={classnames(styles.tbodyTd, styles.borderBottom)}>
                            {/* hochrechnungData.months.reduce((total: number, month) =>
                            total + month.vkNettoPercent, 0) */}
                        </td>
                    </tr>
                    {/*
                    <tr>
                        <td className={classnames(styles.tbodyTd1, styles.borderBottom)}>vkNettoTotalSoFar</td>

                        {hochrechnungData.months.map((month, index) => (
                            <td
                                key={month.month}
                                className={classnames(
                                    styles.tbodyTd,
                                    formStyles.dataValueRight,
                                    styles.borderBottom,
                                    index % 2 ? styles.even : styles.odd,
                                )}
                            >
                                {formatPercent(month.vkNettoTotalSoFar, 2)}
                            </td>
                        ))}
                        <td className={classnames(styles.tbodyTd, styles.borderBottom)} />
                    </tr>
                    <tr>
                        <td className={classnames(styles.tbodyTd1, styles.borderBottom)}>vkNettoPercentSoFar</td>

                        {hochrechnungData.months.map((month, index) => (
                            <td
                                key={month.month}
                                className={classnames(
                                    styles.tbodyTd,
                                    formStyles.dataValueRight,
                                    styles.borderBottom,
                                    index % 2 ? styles.even : styles.odd,
                                )}
                            >
                                {formatPercent(month.vkNettoPercentSoFar, 2)}
                            </td>
                        ))}
                        <td className={classnames(styles.tbodyTd, styles.borderBottom)} />
                    </tr>
                    */}
                    <tr>
                        <td className={styles.tbodyTd1}>Netto-Marge pro Monat</td>
                        {hochrechnungData.months.map((month, index) => (
                            <td
                                key={month.month}
                                className={classnames(
                                    styles.tbodyTd,
                                    formStyles.dataValueRight,
                                    index % 2 ? styles.even : styles.odd,
                                    { [styles.extrapolated]: month.nMargeExtrapolated },
                                )}
                            >
                                {formatMoney(month.nMarge)}
                            </td>
                        ))}
                        <td
                            className={classnames(styles.tbodyTd, formStyles.dataValueRight, {
                                [styles.extrapolated]: current,
                            })}
                        >
                            {formatMoney(hochrechnungData.nMargeTotal)}
                        </td>
                    </tr>
                    <tr>
                        <td className={classnames(styles.tbodyTd1, styles.borderBottom)} />

                        {hochrechnungData.months.map((month, index) => (
                            <td
                                key={month.month}
                                className={classnames(
                                    styles.tbodyTd,
                                    formStyles.dataValueRight,
                                    styles.borderBottom,
                                    index % 2 ? styles.even : styles.odd,
                                    { [styles.extrapolated]: month.nMargePercentExtrapolated },
                                )}
                            >
                                {formatPercent(month.nMargePercent, 2)}
                            </td>
                        ))}
                        <td className={classnames(styles.tbodyTd, styles.borderBottom)}>
                            {/* hochrechnungData.months.reduce((total: number, month) =>
                            total + month.nMargePercent, 0) */}
                        </td>
                    </tr>
                    {/*
                    <tr>
                        <td className={classnames(styles.tbodyTd1, styles.borderBottom)}>nMargeTotalSoFar</td>

                        {hochrechnungData.months.map((month, index) => (
                            <td
                                key={month.month}
                                className={classnames(
                                    styles.tbodyTd,
                                    formStyles.dataValueRight,
                                    styles.borderBottom,
                                    index % 2 ? styles.even : styles.odd,
                                )}
                            >
                                {formatPercent(month.nMargeTotalSoFar, 2)}
                            </td>
                        ))}
                        <td className={classnames(styles.tbodyTd, styles.borderBottom)} />
                    </tr>
                    <tr>
                        <td className={classnames(styles.tbodyTd1, styles.borderBottom)}>vkNettoPercentSoFar</td>

                        {hochrechnungData.months.map((month, index) => (
                            <td
                                key={month.month}
                                className={classnames(
                                    styles.tbodyTd,
                                    formStyles.dataValueRight,
                                    styles.borderBottom,
                                    index % 2 ? styles.even : styles.odd,
                                )}
                            >
                                {formatPercent(month.vkNettoPercentSoFar, 2)}
                            </td>
                        ))}
                        <td className={classnames(styles.tbodyTd, styles.borderBottom)} />
                    </tr>
                    */}
                </tbody>
            </table>
        );
    };

    const chartData = React.useMemo<ChartDataRow[]>(() => {
        if (hochrechnungDataJ0.loading || hochrechnungDataJ1.loading) {
            return [];
        }

        return hochrechnungDataJ0.months.map((month0, index) => {
            const month1 = hochrechnungDataJ1.months[index];

            return {
                month: month0.month.substring(5, 7),
                vkNetto0: month0.vkNetto,
                vkNettoPercent0: month0.vkNettoPercent,
                vkNetto1: month1.vkNetto,
                vkNettoPercent1: month1.vkNettoPercent,
                nMarge0: month0.nMarge,
                nMargePercent0: month0.nMargePercent,
                nMarge1: month1.nMarge,
                nMargePercent1: month1.nMargePercent,
            };
        });
    }, [hochrechnungDataJ0, hochrechnungDataJ1]);

    const getTooltip = (tooltipProps: TooltipProps<number, string>): React.ReactElement | null => {
        if (tooltipProps.payload && tooltipProps.payload.length > 0) {
            const entry = tooltipProps.payload[0].payload as ChartDataRow;

            return (
                <div className={styles.tooltip}>
                    <table>
                        <tbody>
                            <tr>
                                <td />
                                <td className={styles.tooltipHeader}>Aktuelles Jahr</td>
                                <td className={styles.tooltipHeader}>Vorjahr</td>
                            </tr>
                            <tr>
                                <td>VK-Netto</td>
                                <td className={styles.tooltipValue}>{formatMoney(entry.vkNetto0)}</td>
                                <td className={styles.tooltipValue}>{formatMoney(entry.vkNetto1)}</td>
                            </tr>
                            <tr>
                                <td />
                                <td className={styles.tooltipValue}>{formatPercent(entry.vkNettoPercent0, 2)}</td>
                                <td className={styles.tooltipValue}>{formatPercent(entry.vkNettoPercent1, 2)}</td>
                            </tr>
                            <tr>
                                <td>Netto-Marge</td>
                                <td className={styles.tooltipValue}>{formatMoney(entry.nMarge0)}</td>
                                <td className={styles.tooltipValue}>{formatMoney(entry.nMarge1)}</td>
                            </tr>
                            <tr>
                                <td />
                                <td className={styles.tooltipValue}>{formatPercent(entry.nMargePercent0, 2)}</td>
                                <td className={styles.tooltipValue}>{formatPercent(entry.nMargePercent1, 2)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            );
        }

        return null;
    };

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <div className={printView ? styles.printContainer : styles.container}>
            {(hochrechnungDataJ0.loading || hochrechnungDataJ1.loading) && <LoadingIndicator />}
            {printView && (
                <div
                    dangerouslySetInnerHTML={{
                        __html:
                            // eslint-disable-next-line max-len
                            '<style>html, body, #root { overflow: unset !important; height: unset !important; }</style>',
                    }}
                />
            )}
            <h1>
                Hochrechnung - {sourceEntityLabel}
                {' (' +
                    moment(datumVon).format('DD.MM.YYYY') +
                    ' bis ' +
                    moment(datumVon).add({ years: 1 }).subtract({ days: 1 }).format('DD.MM.YYYY') +
                    ')'}
            </h1>
            {printView && <FilterInfo filterValues={filterValues} />}
            {renderSummary()}
            {renderHochrechnungData(hochrechnungDataJ0, true)}
            {renderHochrechnungData(hochrechnungDataJ1, false)}
            <div className={classnames(styles.betreuerSearch)}>
                <div />
                <BetreuerById
                    value={betreuerId}
                    label="Betreuer"
                    emptyLabel="Alle Betreuer"
                    renderLabel="placeholder"
                    allowEmpty
                    onChange={(newValue): void => {
                        setBetreuerId(newValue);
                    }}
                />
            </div>
            <div className={styles.kundeListContainer}>
                <div>
                    <h3 className={styles.kundeListTitle}>
                        Kunden ohne {sourceEntityLabelPlural} im Vorjahr ({kundenOnlyJ0.length})
                    </h3>
                    <span className={styles.kundeListSubTitle}>
                        {' (' +
                            moment(datumVon).subtract({ years: 1 }).format('DD.MM.YYYY') +
                            ' bis ' +
                            moment(hochrechnungDataJ0.proportionalEndMonth + '-01')
                                .endOf('month')
                                .subtract({ years: 1 })
                                .format('DD.MM.YYYY') +
                            ')'}
                    </span>
                    {renderKundeList(kundenOnlyJ0)}
                </div>
                <div>
                    <h3 className={styles.kundeListTitle}>
                        Kunden mit {sourceEntityLabelPlural2} in beiden Jahren ({kundenBoth.length})
                    </h3>
                    <span className={styles.kundeListSubTitle}>&nbsp;</span>
                    {renderKundeList(kundenBoth)}
                </div>
                <div>
                    <h3 className={styles.kundeListTitle}>
                        Kunden ohne {sourceEntityLabelPlural} im aktuellen Jahr ({kundenOnlyJ1.length})
                    </h3>
                    <span className={styles.kundeListSubTitle}>
                        {' (' +
                            moment(datumVon).format('DD.MM.YYYY') +
                            ' bis ' +
                            moment(hochrechnungDataJ0.proportionalEndMonth + '-01')
                                .endOf('month')
                                .format('DD.MM.YYYY') +
                            ')'}
                    </span>
                    {renderKundeList(kundenOnlyJ1)}
                </div>
            </div>
            <div className={styles.chart}>
                <AutoSizer>
                    {({ height, width }) => {
                        return (
                            <BarChart
                                width={width}
                                height={height}
                                data={chartData}
                                margin={{
                                    top: 30,
                                    right: 30,
                                    left: 20,
                                    bottom: 5,
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis />
                                <Tooltip content={getTooltip} />
                                <Legend />
                                <Bar dataKey="vkNetto0" name="VK-Netto Aktuelles Jahr" fill="blue" />
                                <Bar dataKey="vkNetto1" name="VK-Netto Vorjahr" fill="lightblue" />
                                <Bar dataKey="nMarge0" name="Netto-Marge Aktuelles Jahr" fill="green" />
                                <Bar dataKey="nMarge1" name="Netto-Marge Vorjahr" fill="lightgreen" />
                            </BarChart>
                        );
                    }}
                </AutoSizer>
            </div>
        </div>
    );
};
